import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtCurrency, fmtDate } from "../../lib/api";
import MobileHeader from "../../components/MobileHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { toast } from "sonner";
import { useI18n } from "../../lib/i18n";
import {
    Target, PiggyBank, Users as UsersIcon, Plus, ArrowUpRight, ArrowDownRight,
    Lock, Clock, CheckCircle2, TrendingUp, Sparkles,
} from "lucide-react";

const PRODUCT_ICONS = { goals: Target, deposits: PiggyBank, chamas: UsersIcon };
const PRODUCT_THEMES = {
    goals:    { color: "#0066FF", grad: "from-[#0066FF] to-[#0044CC]", labelKey: "savings.goal.label" },
    deposits: { color: "#7000FF", grad: "from-[#7000FF] to-[#5d00d6]", labelKey: "savings.dep.label" },
    chamas:   { color: "#00D084", grad: "from-[#00D084] to-[#00b073]", labelKey: "savings.chama.label" },
};

export default function Savings() {
    const nav = useNavigate();
    const { t } = useI18n();
    const [tab, setTab] = useState("goals");
    const [overview, setOverview] = useState(null);
    const [goals, setGoals] = useState([]);
    const [deposits, setDeposits] = useState([]);
    const [chamas, setChamas] = useState([]);
    const [publicChamas, setPublicChamas] = useState([]);
    const [catalog, setCatalog] = useState(null);
    const [goalDlg, setGoalDlg] = useState(false);
    const [depDlg, setDepDlg] = useState(false);
    const [chamaDlg, setChamaDlg] = useState(false);

    const load = async () => {
        const [o, g, d, c, pc, cat] = await Promise.all([
            api.get("/savings/overview"),
            api.get("/savings/goals"),
            api.get("/savings/deposits"),
            api.get("/savings/chamas"),
            api.get("/savings/chamas/public"),
            api.get("/savings/catalog"),
        ]);
        setOverview(o.data);
        setGoals(g.data);
        setDeposits(d.data);
        setChamas(c.data);
        setPublicChamas(pc.data);
        setCatalog(cat.data);
    };

    useEffect(() => { load(); }, []);

    if (!overview || !catalog) return <div className="p-12 text-center text-slate-400">Loading…</div>;

    return (
        <div data-testid="savings-page">
            <MobileHeader title={t("savings.title")} backTo="/consumer" variant="emerald">
                <div className="mt-4 grid grid-cols-3 gap-2">
                    <KpiMini label={t("savings.kpi.total_saved")} value={fmtCurrency(overview.totals.grand_total, "NGN", { compact: true })} />
                    <KpiMini label={t("savings.kpi.projected_interest")} value={fmtCurrency(overview.totals.projected_interest, "NGN", { compact: true })} accent />
                    <KpiMini label={t("savings.kpi.buckets")} value={overview.goal_count + overview.deposit_count + overview.chama_count} />
                </div>
            </MobileHeader>

            <div className="px-5 -mt-4 pb-4 space-y-4">
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList className="bg-white border border-slate-200 rounded-xl p-1 grid grid-cols-3 w-full" data-testid="savings-tabs">
                        <TabsTrigger value="goals" data-testid="tab-goals"><Target size={14} /> {t("savings.tab.goals")} ({goals.length})</TabsTrigger>
                        <TabsTrigger value="deposits" data-testid="tab-deposits"><PiggyBank size={14} /> {t("savings.tab.deposits")} ({deposits.filter(d => d.status === "active").length})</TabsTrigger>
                        <TabsTrigger value="chamas" data-testid="tab-chamas"><UsersIcon size={14} /> {t("savings.tab.chamas")} ({chamas.length})</TabsTrigger>
                    </TabsList>
                </Tabs>

                {tab === "goals" && (
                    <>
                        <Button onClick={() => setGoalDlg(true)} className="w-full h-12 rounded-xl bg-[#0066FF] hover:bg-[#0052cc] font-semibold" data-testid="create-goal-btn">
                            <Plus size={16} /> {t("savings.goal.new")}
                        </Button>
                        {goals.length === 0 && <EmptyState theme="goals" hint={t("savings.goal.empty")} />}
                        <div className="space-y-3">
                            {goals.map((g) => <GoalCard key={g.id} goal={g} onReload={load} />)}
                        </div>
                    </>
                )}

                {tab === "deposits" && (
                    <>
                        <Button onClick={() => setDepDlg(true)} className="w-full h-12 rounded-xl bg-[#7000FF] hover:bg-[#5d00d6] font-semibold" data-testid="create-deposit-btn">
                            <Plus size={16} /> {t("savings.dep.new")}
                        </Button>
                        <div className="grid grid-cols-2 gap-2">
                            {catalog.fixed_deposit_terms.map((tt) => (
                                <div key={tt.term_days} className="bg-white rounded-2xl p-3 border border-slate-200/60 text-center product-shadow" data-testid={`term-tile-${tt.term_days}`}>
                                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{tt.label}</div>
                                    <div className="font-display text-2xl font-bold text-[#7000FF] mt-1">{(tt.apr * 100).toFixed(0)}%</div>
                                    <div className="text-[10px] text-slate-500">APR</div>
                                </div>
                            ))}
                        </div>
                        {deposits.length === 0 && <EmptyState theme="deposits" hint={t("savings.dep.empty")} />}
                        <div className="space-y-3">
                            {deposits.map((d) => <DepositCard key={d.id} dep={d} onReload={load} />)}
                        </div>
                    </>
                )}

                {tab === "chamas" && (
                    <>
                        <Button onClick={() => setChamaDlg(true)} className="w-full h-12 rounded-xl bg-[#00D084] hover:bg-[#00b073] font-semibold" data-testid="create-chama-btn">
                            <Plus size={16} /> {t("savings.chama.new")}
                        </Button>
                        {chamas.length === 0 && publicChamas.length === 0 && <EmptyState theme="chamas" hint={t("savings.chama.empty")} />}
                        <div className="space-y-3">
                            {chamas.map((c) => <ChamaCard key={c.id} chama={c} isMember={true} onReload={load} />)}
                            {publicChamas.filter(pc => !pc.is_member).length > 0 && (
                                <>
                                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500 pt-3">{t("savings.chama.open_to_join")}</div>
                                    {publicChamas.filter(pc => !pc.is_member).map((c) => <ChamaCard key={c.id} chama={c} isMember={false} onReload={load} />)}
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>

            <NewGoalDialog open={goalDlg} onClose={() => setGoalDlg(false)} catalog={catalog} onDone={load} />
            <NewDepositDialog open={depDlg} onClose={() => setDepDlg(false)} catalog={catalog} onDone={load} />
            <NewChamaDialog open={chamaDlg} onClose={() => setChamaDlg(false)} onDone={load} />
        </div>
    );
}

function KpiMini({ label, value, accent }) {
    return (
        <div className="bg-white/15 backdrop-blur rounded-xl px-2 py-2 text-center">
            <div className="text-[10px] text-white/80 uppercase tracking-wider">{label}</div>
            <div className={`text-sm font-bold ${accent ? "text-emerald-200" : "text-white"} truncate`}>{value}</div>
        </div>
    );
}

function EmptyState({ theme, hint }) {
    const { t } = useI18n();
    const meta = PRODUCT_THEMES[theme];
    const Icon = PRODUCT_ICONS[theme];
    return (
        <div className="bg-white rounded-2xl border border-slate-200/60 p-8 text-center product-shadow">
            <div className="size-14 rounded-2xl mx-auto flex items-center justify-center text-white" style={{ background: meta.color }}>
                <Icon size={24} />
            </div>
            <div className="font-display font-semibold text-[#0A1930] mt-3">{t(meta.labelKey)}</div>
            <p className="text-xs text-slate-500 mt-1">{hint}</p>
        </div>
    );
}

function GoalCard({ goal, onReload }) {
    const { t } = useI18n();
    const [amount, setAmount] = useState("");
    const [busy, setBusy] = useState(false);
    const progress = Math.min(100, (goal.current_amount / goal.target_amount) * 100);
    const completed = goal.status === "completed";

    const contribute = async () => {
        const amt = parseFloat(amount);
        if (!amt || amt <= 0) return toast.error("Enter an amount");
        setBusy(true);
        try {
            await api.post("/savings/goals/contribute", { goal_id: goal.id, amount: amt });
            toast.success(`Added ${fmtCurrency(amt)} to ${goal.name}`);
            setAmount("");
            onReload();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    const withdrawAll = async () => {
        if (!confirm(`Withdraw ${fmtCurrency(goal.current_amount)} from ${goal.name}?`)) return;
        try {
            await api.post("/savings/goals/withdraw", { goal_id: goal.id, amount: goal.current_amount });
            toast.success("Withdrawn to wallet");
            onReload();
        } catch (err) { toast.error("Failed"); }
    };

    return (
        <div className="bg-white rounded-2xl p-4 border border-slate-200/60 product-shadow" data-testid={`goal-${goal.id}`}>
            <div className="flex items-start gap-3">
                <div className="size-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0" style={{ background: `${goal.color}15` }}>{goal.emoji}</div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                        <div className="font-display font-semibold text-[#0A1930] truncate">{goal.name}</div>
                        {completed && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200"><CheckCircle2 size={11} /> {t("savings.goal.reached")}</Badge>}
                    </div>
                    <div className="flex justify-between text-xs text-slate-500 mt-0.5">
                        <span className="font-mono font-bold text-[#0A1930]">{fmtCurrency(goal.current_amount)}</span>
                        <span>of {fmtCurrency(goal.target_amount)}</span>
                    </div>
                    <Progress value={progress} className="h-2 mt-1.5" style={{ "--progress-color": goal.color }} />
                    {goal.auto_route_percent > 0 && (
                        <div className="text-[10px] text-slate-400 mt-1.5 flex items-center gap-1">
                            <Sparkles size={10} /> {t("savings.goal.auto_route", { pct: goal.auto_route_percent })}
                        </div>
                    )}
                </div>
            </div>
            {!completed && (
                <div className="mt-3 flex gap-2">
                    <Input type="number" placeholder="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-9 rounded-lg text-sm" data-testid={`goal-amount-${goal.id}`} />
                    <Button onClick={contribute} disabled={busy} size="sm" className="h-9 rounded-lg" style={{ background: goal.color }} data-testid={`goal-contribute-${goal.id}`}>
                        <ArrowUpRight size={14} /> {t("savings.goal.add")}
                    </Button>
                </div>
            )}
            {goal.current_amount > 0 && (
                <button onClick={withdrawAll} className="mt-2 text-[11px] text-slate-500 hover:text-rose-600 inline-flex items-center gap-1" data-testid={`goal-withdraw-${goal.id}`}>
                    <ArrowDownRight size={11} /> {t("savings.goal.withdraw_all")}
                </button>
            )}
        </div>
    );
}

function DepositCard({ dep, onReload }) {
    const { t } = useI18n();
    const breakDep = async () => {
        if (!confirm("Break deposit early? You'll lose a portion of accrued interest.")) return;
        try {
            const r = await api.post("/savings/deposits/break", { deposit_id: dep.id });
            toast.success(`Paid out ${fmtCurrency(r.data.payout)} (penalty: ${fmtCurrency(r.data.penalty)})`);
            onReload();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    };

    const STATUS_BG = {
        active:  "bg-blue-50 text-blue-700 border-blue-200",
        matured: "bg-emerald-50 text-emerald-700 border-emerald-200",
        broken:  "bg-rose-50 text-rose-700 border-rose-200",
    };

    return (
        <div className="bg-white rounded-2xl p-4 border border-slate-200/60 product-shadow" data-testid={`deposit-${dep.id}`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="font-display font-semibold text-[#0A1930]">{dep.term_label} · {(dep.apr * 100).toFixed(2).replace(/\.?0+$/, "")}% APR</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                        <Lock size={10} /> {t("savings.dep.locked_until", { date: fmtDate(dep.maturity_date) })}
                    </div>
                    {dep.apr_boost > 0 && (
                        <div
                            className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-md bg-emerald-50 border border-emerald-200 text-[9px] font-bold text-emerald-700 uppercase tracking-wider"
                            data-testid={`deposit-boost-${dep.id}`}
                        >
                            <Sparkles size={9} /> {dep.tier_at_purchase} +{(dep.apr_boost * 100).toFixed(2).replace(/\.?0+$/, "")}% boost
                        </div>
                    )}
                </div>
                <Badge className={`capitalize border ${STATUS_BG[dep.status]}`}>{dep.status}</Badge>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 pt-3 border-t border-slate-100">
                <Mini label={t("savings.dep.principal")} value={fmtCurrency(dep.amount, "NGN", { compact: true })} />
                <Mini label={t("savings.dep.interest")} value={`+${fmtCurrency(dep.interest, "NGN", { compact: true })}`} valueClass="text-emerald-600" />
                <Mini label={t("savings.dep.at_maturity")} value={fmtCurrency(dep.matured_amount, "NGN", { compact: true })} valueClass="text-[#7000FF]" />
            </div>
            {dep.status === "active" && (
                <Button onClick={breakDep} variant="outline" size="sm" className="w-full mt-3 h-9 rounded-lg text-xs border-rose-200 text-rose-600 hover:bg-rose-50" data-testid={`deposit-break-${dep.id}`}>
                    {t("savings.dep.break_early")}
                </Button>
            )}
        </div>
    );
}

function ChamaCard({ chama, isMember, onReload }) {
    const { t } = useI18n();
    const join = async () => {
        try {
            await api.post("/savings/chamas/join", { chama_id: chama.id });
            toast.success(`Joined ${chama.name}`);
            onReload();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    };

    const contribute = async () => {
        try {
            const r = await api.post("/savings/chamas/contribute", { chama_id: chama.id });
            if (r.data.payout_event) {
                toast.success(`${r.data.payout_event.recipient_name} received ${fmtCurrency(r.data.payout_event.amount)} payout!`);
            } else {
                toast.success(`Contributed ${fmtCurrency(chama.contribution_amount)} to ${chama.name}`);
            }
            onReload();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    };

    return (
        <div className="bg-white rounded-2xl p-4 border border-slate-200/60 product-shadow" data-testid={`chama-${chama.id}`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="font-display font-semibold text-[#0A1930]">{chama.name}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                        {chama.member_count} members · {chama.frequency} · {fmtCurrency(chama.contribution_amount, "NGN", { compact: true })}/cycle
                    </div>
                </div>
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 capitalize">{chama.frequency}</Badge>
            </div>
            {chama.description && <p className="text-xs text-slate-600 mt-2">{chama.description}</p>}
            <div className="mt-3 grid grid-cols-3 gap-2 pt-3 border-t border-slate-100">
                <Mini label={t("savings.chama.pool_now")} value={fmtCurrency(chama.pool_balance, "NGN", { compact: true })} valueClass="text-emerald-600" />
                {isMember && <Mini label={t("savings.chama.my_contribution")} value={fmtCurrency(chama.my_contributed, "NGN", { compact: true })} />}
                <Mini label={t("savings.chama.target")} value={fmtCurrency(chama.contribution_amount * chama.member_count, "NGN", { compact: true })} valueClass="text-[#0066FF]" />
            </div>
            {isMember ? (
                <Button onClick={contribute} size="sm" className="w-full mt-3 h-9 rounded-lg bg-[#00D084] hover:bg-[#00b073] text-xs" data-testid={`chama-contribute-${chama.id}`}>
                    <ArrowUpRight size={14} /> {t("savings.chama.contribute", { amount: fmtCurrency(chama.contribution_amount) })}
                </Button>
            ) : (
                <Button onClick={join} size="sm" variant="outline" className="w-full mt-3 h-9 rounded-lg text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid={`chama-join-${chama.id}`}>
                    {t("savings.chama.join")}
                </Button>
            )}
        </div>
    );
}

function Mini({ label, value, valueClass }) {
    return (
        <div className="text-center">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
            <div className={`font-mono text-xs font-bold mt-0.5 ${valueClass || "text-[#0A1930]"}`}>{value}</div>
        </div>
    );
}

function NewGoalDialog({ open, onClose, catalog, onDone }) {
    const { t } = useI18n();
    const [form, setForm] = useState({ name: "", template: "emergency", target_amount: "", auto_route_percent: 0 });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!form.name || !form.target_amount) return toast.error("Fill all fields");
        setBusy(true);
        try {
            await api.post("/savings/goals", { ...form, target_amount: parseFloat(form.target_amount), auto_route_percent: parseFloat(form.auto_route_percent || 0) });
            toast.success("Goal created");
            setForm({ name: "", template: "emergency", target_amount: "", auto_route_percent: 0 });
            onDone(); onClose();
        } catch (err) { toast.error("Failed"); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-2xl max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2"><Target size={18} className="text-[#0066FF]" /> {t("savings.dlg.goal.title")}</DialogTitle>
                    <DialogDescription>{t("savings.dlg.goal.desc")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">{t("savings.dlg.goal.template")}</Label>
                        <div className="grid grid-cols-4 gap-2 mt-1.5">
                            {catalog.goal_templates.map((tpl) => (
                                <button key={tpl.key} onClick={() => setForm((s) => ({ ...s, template: tpl.key, name: s.name || tpl.name }))}
                                    className={`p-2 rounded-xl border text-xs flex flex-col items-center gap-1 ${form.template === tpl.key ? "border-[#0066FF] bg-blue-50" : "border-slate-200"}`}
                                    data-testid={`template-${tpl.key}`}>
                                    <span className="text-lg">{tpl.emoji}</span>
                                    <span className="text-[10px] truncate">{tpl.name}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">{t("savings.dlg.goal.name")}</Label>
                        <Input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} className="mt-1 h-11 rounded-xl" data-testid="goal-name-input" />
                    </div>
                    <div>
                        <Label className="text-xs">{t("savings.dlg.goal.target")}</Label>
                        <Input type="number" value={form.target_amount} onChange={(e) => setForm((s) => ({ ...s, target_amount: e.target.value }))} className="mt-1 h-11 rounded-xl" data-testid="goal-target-input" />
                    </div>
                    <div>
                        <Label className="text-xs">{t("savings.dlg.goal.auto")}</Label>
                        <Input type="number" value={form.auto_route_percent} onChange={(e) => setForm((s) => ({ ...s, auto_route_percent: e.target.value }))} className="mt-1 h-11 rounded-xl" data-testid="goal-auto-input" />
                    </div>
                    <Button onClick={submit} disabled={busy} className="w-full h-11 rounded-xl bg-[#0066FF] hover:bg-[#0052cc]" data-testid="goal-submit-btn">
                        {busy ? "…" : t("savings.dlg.goal.submit")}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function NewDepositDialog({ open, onClose, catalog, onDone }) {
    const { t } = useI18n();
    const [amount, setAmount] = useState("");
    const [term, setTerm] = useState(90);
    const [busy, setBusy] = useState(false);
    const termMeta = catalog.fixed_deposit_terms.find((tt) => tt.term_days === term);
    const amt = parseFloat(amount || 0);
    const projected = termMeta ? amt * (termMeta.apr * term / 365.0) : 0;

    const submit = async () => {
        if (!amount || amt <= 0) return toast.error("Enter amount");
        setBusy(true);
        try {
            await api.post("/savings/deposits", { amount: amt, term_days: term });
            toast.success(`Deposit locked. Earn ${fmtCurrency(projected)} at maturity!`);
            setAmount("");
            onDone(); onClose();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-2xl max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2"><PiggyBank size={18} className="text-[#7000FF]" /> {t("savings.dlg.dep.title")}</DialogTitle>
                    <DialogDescription>{t("savings.dlg.dep.desc")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">{t("savings.dlg.dep.term")}</Label>
                        <div className="grid grid-cols-2 gap-2 mt-1.5">
                            {catalog.fixed_deposit_terms.map((tt) => (
                                <button key={tt.term_days} onClick={() => setTerm(tt.term_days)}
                                    className={`p-3 rounded-xl border text-center ${term === tt.term_days ? "border-[#7000FF] bg-purple-50" : "border-slate-200"}`}
                                    data-testid={`dep-term-${tt.term_days}`}>
                                    <div className="font-display text-2xl font-bold text-[#7000FF]">{(tt.apr * 100).toFixed(0)}%</div>
                                    <div className="text-[10px] text-slate-500 uppercase font-bold">{tt.label} APR</div>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">{t("savings.dlg.dep.amount")}</Label>
                        <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="dep-amount-input" />
                    </div>
                    {amt > 0 && (
                        <div className="bg-purple-50 rounded-xl p-3 text-sm">
                            <div className="flex justify-between"><span className="text-slate-600">{t("savings.dlg.dep.projected")}</span><span className="font-bold text-[#7000FF]">+{fmtCurrency(projected)}</span></div>
                            <div className="flex justify-between mt-1"><span className="text-slate-600">{t("savings.dlg.dep.payout")}</span><span className="font-bold">{fmtCurrency(amt + projected)}</span></div>
                        </div>
                    )}
                    <Button onClick={submit} disabled={busy} className="w-full h-11 rounded-xl bg-[#7000FF] hover:bg-[#5d00d6]" data-testid="dep-submit-btn">
                        {busy ? "…" : t("savings.dlg.dep.submit")}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function NewChamaDialog({ open, onClose, onDone }) {
    const { t } = useI18n();
    const [form, setForm] = useState({ name: "", contribution_amount: "", frequency: "monthly", description: "" });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!form.name || !form.contribution_amount) return toast.error("Fill required fields");
        setBusy(true);
        try {
            await api.post("/savings/chamas", { ...form, contribution_amount: parseFloat(form.contribution_amount) });
            toast.success("Chama created!");
            setForm({ name: "", contribution_amount: "", frequency: "monthly", description: "" });
            onDone(); onClose();
        } catch (err) { toast.error("Failed"); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-2xl max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2"><UsersIcon size={18} className="text-[#00D084]" /> {t("savings.dlg.chama.title")}</DialogTitle>
                    <DialogDescription>{t("savings.dlg.chama.desc")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">{t("savings.dlg.chama.name")}</Label>
                        <Input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} placeholder="e.g. Family Chama" className="mt-1 h-11 rounded-xl" data-testid="chama-name-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-xs">{t("savings.dlg.chama.contribution")}</Label>
                            <Input type="number" value={form.contribution_amount} onChange={(e) => setForm((s) => ({ ...s, contribution_amount: e.target.value }))} className="mt-1 h-11 rounded-xl" data-testid="chama-amount-input" />
                        </div>
                        <div>
                            <Label className="text-xs">{t("savings.dlg.chama.freq")}</Label>
                            <Select value={form.frequency} onValueChange={(v) => setForm((s) => ({ ...s, frequency: v }))}>
                                <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid="chama-freq-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="weekly">{t("savings.dlg.chama.weekly")}</SelectItem>
                                    <SelectItem value="monthly">{t("savings.dlg.chama.monthly")}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">{t("savings.dlg.chama.desc_field")}</Label>
                        <Input value={form.description} onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))} placeholder="What's this chama for?" className="mt-1 h-11 rounded-xl" data-testid="chama-desc-input" />
                    </div>
                    <Button onClick={submit} disabled={busy} className="w-full h-11 rounded-xl bg-[#00D084] hover:bg-[#00b073]" data-testid="chama-submit-btn">
                        {busy ? "…" : t("savings.dlg.chama.submit")}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
